/**
 * PMS Integrations page — manage connections to external Property Management Systems
 * (Smoobu, Avaibook REST, Hostaway, etc.). Pulls bookings + auto-creates ZENTRA properties.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Plus,
    Plug,
    RefreshCw,
    CheckCircle2,
    AlertCircle,
    Trash2,
    Pause,
    Play,
    Sparkles,
    Building2,
    KeyRound,
    BookOpen,
    ExternalLink,
    Phone,
    Save,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Modal from "@/components/Modal";
import EmptyState from "@/components/EmptyState";

export default function Integrations() {
    const [connectors, setConnectors] = useState([]);
    const [connections, setConnections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);
    const [editConnection, setEditConnection] = useState(null);
    const [syncResult, setSyncResult] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [cs, cn] = await Promise.all([api.get("/pms/connectors"), api.get("/pms/connections")]);
            setConnectors(cs.data || []);
            setConnections(cn.data || []);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const connectorMap = useMemo(() => Object.fromEntries(connectors.map((c) => [c.type, c])), [connectors]);

    const test = async (conn) => {
        setBusyId(conn.id);
        try {
            const { data } = await api.post(`/pms/connections/${conn.id}/test`);
            alert(data.ok ? `✓ ${data.message || "Conexión válida"}${data.account_label ? `\n${data.account_label}` : ""}` : `✗ ${data.message}`);
        } catch (e) {
            alert(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    const syncNow = async (conn) => {
        setBusyId(conn.id);
        setSyncResult(null);
        try {
            const { data } = await api.post(`/pms/connections/${conn.id}/sync`);
            setSyncResult({ ...data, connection_name: conn.name });
            await load();
        } catch (e) {
            alert(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    const toggleEnabled = async (conn) => {
        setBusyId(conn.id);
        try {
            await api.patch(`/pms/connections/${conn.id}`, { enabled: !conn.enabled });
            await load();
        } finally {
            setBusyId(null);
        }
    };

    const remove = async (conn) => {
        if (!window.confirm(`¿Eliminar la conexión "${conn.name}"? Las propiedades y reservas ya importadas se mantienen.`))
            return;
        await api.delete(`/pms/connections/${conn.id}`);
        await load();
    };

    return (
        <div className="space-y-6" data-testid="integrations-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="text-[11px] font-semibold tracking-widest uppercase text-zentra-accent-strong">
                        Conectores externos
                    </p>
                    <h2 className="font-display text-3xl font-bold text-zentra-ink tracking-tight mt-1">
                        Integraciones PMS
                    </h2>
                    <p className="text-sm text-zentra-muted mt-1">
                        Conecta ZENTRA con tu Property Management System usando un token API. Las reservas se importan
                        cada 30 min automáticamente (incluye email + teléfono del huésped).
                    </p>
                </div>
                <button
                    type="button"
                    className="zentra-btn-primary"
                    onClick={() => setAddOpen(true)}
                    data-testid="add-pms-connection"
                >
                    <Plus size={16} /> Nueva conexión
                </button>
            </div>

            <OfficeSettingsCard />

            <IcalImporterCard />

            <BotsPmsCard />

            {syncResult && (
                <div className="zentra-card-static p-4 border-l-4 border-zentra-accent" data-testid="last-sync-result">
                    <div className="flex items-start gap-3">
                        <Sparkles size={18} className="text-zentra-accent-strong mt-0.5" />
                        <div className="flex-1">
                            <p className="font-display font-semibold text-zentra-ink">
                                Sincronización completada · {syncResult.connection_name}
                            </p>
                            <p className="text-sm text-zentra-muted mt-1">
                                {syncResult.properties_created} propiedades nuevas creadas ·{" "}
                                {syncResult.properties_seen} propiedades vistas en el PMS ·{" "}
                                {syncResult.bookings_imported} reservas importadas ·{" "}
                                {syncResult.tasks_created} tareas de limpieza auto-generadas
                                {syncResult.error && (
                                    <span className="block text-red-700 mt-1">Error: {syncResult.error}</span>
                                )}
                            </p>
                        </div>
                        <button
                            type="button"
                            className="text-zentra-muted hover:text-zentra-ink text-xs"
                            onClick={() => setSyncResult(null)}
                        >
                            cerrar
                        </button>
                    </div>
                </div>
            )}

            {/* Connection list */}
            {loading ? (
                <div className="zentra-skeleton h-48" />
            ) : connections.length === 0 ? (
                <EmptyState
                    icon={Plug}
                    title="Sin conexiones PMS"
                    description="Añade una conexión a Smoobu, Avaibook, Hostaway u otro PMS para sincronizar reservas automáticamente."
                    action={
                        <button onClick={() => setAddOpen(true)} className="zentra-btn-primary mt-2">
                            <Plus size={14} /> Añadir conexión
                        </button>
                    }
                />
            ) : (
                <div className="grid md:grid-cols-2 gap-4" data-testid="connection-grid">
                    {connections.map((conn) => (
                        <ConnectionCard
                            key={conn.id}
                            conn={conn}
                            connector={connectorMap[conn.connector_type]}
                            busy={busyId === conn.id}
                            onTest={() => test(conn)}
                            onSync={() => syncNow(conn)}
                            onToggle={() => toggleEnabled(conn)}
                            onEdit={() => setEditConnection(conn)}
                            onDelete={() => remove(conn)}
                        />
                    ))}
                </div>
            )}

            {/* Available connectors info */}
            <section className="zentra-card-static p-5" data-testid="available-connectors">
                <h3 className="font-display font-semibold text-zentra-ink mb-3">Conectores disponibles</h3>
                <p className="text-xs text-zentra-muted mb-4">
                    ZENTRA usa un framework extensible. Para añadir un PMS nuevo, sigue la guía en{" "}
                    <code className="text-zentra-ink bg-zentra-bg/60 px-1.5 py-0.5 rounded">
                        /app/backend/pms_connectors/README.md
                    </code>
                </p>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {connectors.map((c) => (
                        <div
                            key={c.type}
                            className="rounded-md border border-zentra-border bg-white p-3"
                            data-testid={`connector-info-${c.type}`}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <span className="font-display font-semibold text-zentra-ink">{c.label}</span>
                                <a
                                    href={c.docs_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-xs text-zentra-muted hover:text-zentra-primary inline-flex items-center gap-1"
                                >
                                    <BookOpen size={11} /> Docs
                                    <ExternalLink size={10} />
                                </a>
                            </div>
                            <p className="text-xs text-zentra-muted">{c.description}</p>
                        </div>
                    ))}
                </div>
            </section>

            <ConnectionFormModal
                open={addOpen || !!editConnection}
                onClose={() => {
                    setAddOpen(false);
                    setEditConnection(null);
                }}
                connectors={connectors}
                editConn={editConnection}
                onSaved={async (newConn) => {
                    setAddOpen(false);
                    setEditConnection(null);
                    await load();
                    if (newConn && !editConnection) {
                        // Auto-test new connection
                        try {
                            const { data } = await api.post(`/pms/connections/${newConn.id}/test`);
                            if (!data.ok) alert(`Conexión creada pero el test falló: ${data.message}`);
                        } catch (e) {
                            /* ignore */
                        }
                    }
                }}
            />

            {error && (
                <div className="rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div>
            )}
        </div>
    );
}

function OfficeSettingsCard() {
    const [form, setForm] = useState({
        office_name: "",
        office_whatsapp: "",
        office_phone: "",
        office_email: "",
    });
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/settings")
            .then((r) => {
                setForm({
                    office_name: r.data?.office_name || "",
                    office_whatsapp: r.data?.office_whatsapp || "",
                    office_phone: r.data?.office_phone || "",
                    office_email: r.data?.office_email || "",
                });
            })
            .finally(() => setLoaded(true));
    }, []);

    const save = async () => {
        setSaving(true);
        setError("");
        try {
            await api.put("/settings", form);
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    if (!loaded) return null;

    return (
        <div className="zentra-card-static p-5" data-testid="office-settings-card">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <p className="text-[11px] font-semibold tracking-widest uppercase text-zentra-accent-strong">
                        Contacto operativo
                    </p>
                    <h3 className="font-display font-semibold text-zentra-ink mt-1">
                        Datos de la oficina
                    </h3>
                    <p className="text-xs text-zentra-muted mt-1">
                        El número de WhatsApp aparecerá como botón flotante en el Portal del Operario para que las
                        trabajadoras puedan escribirte directamente.
                    </p>
                </div>
                {form.office_whatsapp && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] font-semibold">
                        <CheckCircle2 size={11} /> Botón WhatsApp activo
                    </span>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                <div>
                    <label className="zentra-label">Nombre de la oficina</label>
                    <input
                        className="zentra-input"
                        value={form.office_name}
                        onChange={(e) => setForm({ ...form, office_name: e.target.value })}
                        placeholder="Oficina Valmay"
                        data-testid="office-name"
                    />
                </div>
                <div>
                    <label className="zentra-label flex items-center gap-1.5">
                        <Phone size={11} /> WhatsApp oficina (con prefijo internacional)
                    </label>
                    <input
                        className="zentra-input font-mono-zentra"
                        value={form.office_whatsapp}
                        onChange={(e) => setForm({ ...form, office_whatsapp: e.target.value })}
                        placeholder="+34 600 100 100"
                        data-testid="office-whatsapp"
                    />
                    <p className="text-[10px] text-zentra-muted mt-1">
                        Ejemplo: +34 600 123 456. Si lo dejas vacío, el botón flotante no aparece.
                    </p>
                </div>
                <div>
                    <label className="zentra-label">Teléfono fijo</label>
                    <input
                        className="zentra-input font-mono-zentra"
                        value={form.office_phone}
                        onChange={(e) => setForm({ ...form, office_phone: e.target.value })}
                        placeholder="+34 952 100 100"
                    />
                </div>
                <div>
                    <label className="zentra-label">Email oficina</label>
                    <input
                        type="email"
                        className="zentra-input"
                        value={form.office_email}
                        onChange={(e) => setForm({ ...form, office_email: e.target.value })}
                        placeholder="oficina@zentravalmay.es"
                    />
                </div>
            </div>

            {error && (
                <div className="mt-3 rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div>
            )}

            <div className="mt-4 flex items-center justify-end gap-2">
                {saved && <span className="text-xs text-emerald-700 font-semibold">✓ Guardado</span>}
                <button
                    type="button"
                    onClick={save}
                    className="zentra-btn-primary"
                    disabled={saving}
                    data-testid="save-office-settings"
                >
                    <Save size={14} /> {saving ? "Guardando…" : "Guardar"}
                </button>
            </div>
        </div>
    );
}

function ConnectionCard({ conn, connector, busy, onTest, onSync, onToggle, onEdit, onDelete }) {
    const summary = conn.last_summary;
    return (
        <div className="zentra-card p-5 flex flex-col gap-3" data-testid={`connection-card-${conn.id}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <span
                            className={`w-2 h-2 rounded-full ${
                                conn.last_status === "ok"
                                    ? "bg-emerald-500"
                                    : conn.last_status === "error"
                                      ? "bg-red-500"
                                      : "bg-zentra-muted"
                            }`}
                        />
                        <p className="text-[10px] tracking-widest uppercase text-zentra-muted">
                            {connector?.label || conn.connector_type}
                        </p>
                    </div>
                    <h3 className="font-display font-semibold text-zentra-ink mt-1 truncate">{conn.name}</h3>
                    {conn.last_synced && (
                        <p className="text-[11px] text-zentra-muted mt-1">
                            Última sync: {new Date(conn.last_synced).toLocaleString("es-ES")}
                        </p>
                    )}
                    {conn.last_error && (
                        <p className="text-[11px] text-red-700 mt-1 break-words">{conn.last_error}</p>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={onToggle}
                        disabled={busy}
                        className="w-8 h-8 rounded-md border border-zentra-border bg-white text-zentra-muted hover:text-zentra-primary flex items-center justify-center"
                        title={conn.enabled ? "Pausar (no se sincronizará)" : "Activar"}
                        data-testid={`toggle-connection-${conn.id}`}
                    >
                        {conn.enabled ? <Pause size={13} /> : <Play size={13} />}
                    </button>
                    <button
                        type="button"
                        onClick={onDelete}
                        disabled={busy}
                        className="w-8 h-8 rounded-md border border-red-200 bg-white text-red-600 hover:bg-red-50 flex items-center justify-center"
                        data-testid={`delete-connection-${conn.id}`}
                    >
                        <Trash2 size={13} />
                    </button>
                </div>
            </div>

            {summary && (
                <div className="grid grid-cols-3 gap-2 py-2 border-y border-zentra-border">
                    <Stat label="Propiedades" value={Object.keys(conn.property_mappings || {}).length} icon={Building2} />
                    <Stat label="Reservas" value={summary.bookings_imported || 0} />
                    <Stat label="Tareas auto" value={summary.tasks_created || 0} icon={Sparkles} accent />
                </div>
            )}

            <div className="flex items-center gap-2 flex-wrap">
                <button
                    type="button"
                    onClick={onTest}
                    disabled={busy}
                    className="zentra-btn-ghost text-xs"
                    data-testid={`test-connection-${conn.id}`}
                >
                    <KeyRound size={13} />
                    Test
                </button>
                <button
                    type="button"
                    onClick={onSync}
                    disabled={busy || !conn.enabled}
                    className="zentra-btn-accent text-xs flex-1 justify-center"
                    data-testid={`sync-connection-${conn.id}`}
                >
                    <RefreshCw size={13} className={busy ? "animate-spin" : ""} />
                    {busy ? "Sincronizando…" : "Sincronizar ahora"}
                </button>
                <button
                    type="button"
                    onClick={onEdit}
                    disabled={busy}
                    className="zentra-btn-ghost text-xs"
                    data-testid={`edit-connection-${conn.id}`}
                >
                    Editar
                </button>
            </div>
        </div>
    );
}

function Stat({ label, value, icon: Icon, accent }) {
    return (
        <div className="text-center">
            <div className="flex items-center justify-center gap-1">
                {Icon && <Icon size={11} className={accent ? "text-zentra-accent-strong" : "text-zentra-muted"} />}
                <span className={`font-display font-bold ${accent ? "text-zentra-accent-strong" : "text-zentra-ink"}`}>
                    {value}
                </span>
            </div>
            <p className="text-[9px] uppercase tracking-widest text-zentra-muted mt-0.5">{label}</p>
        </div>
    );
}

function ConnectionFormModal({ open, onClose, connectors, editConn, onSaved }) {
    const [name, setName] = useState("");
    const [connectorType, setConnectorType] = useState("");
    const [creds, setCreds] = useState({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const selectedConnector = useMemo(
        () => connectors.find((c) => c.type === connectorType),
        [connectors, connectorType],
    );

    useEffect(() => {
        if (!open) return;
        if (editConn) {
            setName(editConn.name);
            setConnectorType(editConn.connector_type);
            setCreds({}); // never preload secrets — user re-enters if changing
        } else {
            setName("");
            setConnectorType(connectors[0]?.type || "");
            setCreds({});
        }
        setError("");
    }, [open, editConn, connectors]);

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!name.trim() || !connectorType) {
            setError("Indica nombre y tipo de conector");
            return;
        }
        // Validate required fields are filled
        if (selectedConnector) {
            for (const f of selectedConnector.credentials_schema) {
                if (f.required && !creds[f.key] && !editConn) {
                    setError(`Falta ${f.label}`);
                    return;
                }
            }
        }
        setSaving(true);
        setError("");
        try {
            let saved;
            if (editConn) {
                const payload = { name };
                const filledCreds = Object.fromEntries(Object.entries(creds).filter(([, v]) => v));
                if (Object.keys(filledCreds).length > 0) payload.credentials = filledCreds;
                const { data } = await api.patch(`/pms/connections/${editConn.id}`, payload);
                saved = data;
            } else {
                const { data } = await api.post("/pms/connections", {
                    name,
                    connector_type: connectorType,
                    credentials: creds,
                });
                saved = data;
            }
            onSaved?.(saved);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={editConn ? `Editar conexión · ${editConn.name}` : "Nueva conexión PMS"}
            subtitle={
                editConn
                    ? "Deja los campos de credencial en blanco si no quieres cambiarlos."
                    : "Conecta ZENTRA con tu PMS usando una API key. Las reservas se sincronizan cada 30 min."
            }
            wide
            testId="connection-modal"
            footer={
                <>
                    <button className="zentra-btn-ghost" onClick={onClose} disabled={saving}>
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        form="conn-form"
                        className="zentra-btn-primary"
                        disabled={saving}
                        data-testid="save-connection"
                    >
                        {saving ? "Guardando…" : editConn ? "Guardar cambios" : "Crear conexión"}
                    </button>
                </>
            }
        >
            <form id="conn-form" onSubmit={submit} className="space-y-4">
                <div>
                    <label className="zentra-label">Nombre interno</label>
                    <input
                        className="zentra-input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Smoobu principal"
                        data-testid="connection-name"
                    />
                </div>
                {!editConn && (
                    <div>
                        <label className="zentra-label">Conector</label>
                        <select
                            className="zentra-select"
                            value={connectorType}
                            onChange={(e) => setConnectorType(e.target.value)}
                            data-testid="connection-type"
                        >
                            <option value="">Selecciona…</option>
                            {connectors.map((c) => (
                                <option key={c.type} value={c.type}>
                                    {c.label}
                                </option>
                            ))}
                        </select>
                        {selectedConnector?.description && (
                            <p className="text-xs text-zentra-muted mt-1">{selectedConnector.description}</p>
                        )}
                    </div>
                )}
                {selectedConnector && (
                    <div className="space-y-3 p-3 rounded-md bg-zentra-bg/40 border border-zentra-border">
                        <p className="text-[10px] font-semibold tracking-widest uppercase text-zentra-muted">
                            Credenciales {selectedConnector.label}
                        </p>
                        {selectedConnector.credentials_schema.map((f) => (
                            <div key={f.key}>
                                <label className="zentra-label">
                                    {f.label}
                                    {f.required && <span className="text-red-600 ml-1">*</span>}
                                </label>
                                <input
                                    type={f.type === "password" ? "password" : f.type === "number" ? "number" : "text"}
                                    className="zentra-input"
                                    value={creds[f.key] || ""}
                                    onChange={(e) => setCreds({ ...creds, [f.key]: e.target.value })}
                                    placeholder={f.placeholder}
                                    data-testid={`cred-${f.key}`}
                                />
                                {f.help && <p className="text-[11px] text-zentra-muted mt-1">{f.help}</p>}
                            </div>
                        ))}
                        {selectedConnector.docs_url && (
                            <a
                                href={selectedConnector.docs_url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs text-zentra-accent-strong hover:underline inline-flex items-center gap-1"
                            >
                                Documentación del proveedor
                                <ExternalLink size={11} />
                            </a>
                        )}
                    </div>
                )}
                {error && (
                    <div className="rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">
                        {error}
                    </div>
                )}
            </form>
        </Modal>
    );
}


// ======================================================================
// iCal Importer — bridge solution while waiting for API credentials.
// Each property can hold N iCal feeds (Airbnb, Booking, Vrbo, Avantio, etc).
// Background scheduler syncs every 30 min — manual button forces sync now.
// ======================================================================
const ICAL_SOURCES = [
    { value: "airbnb", label: "Airbnb", help: "Anfitrión → Calendario → Disponibilidad → Exportar calendario" },
    { value: "booking", label: "Booking.com", help: "Extranet → Tarifas y Disponibilidad → Sincronizar Calendarios → Exportar" },
    { value: "vrbo", label: "Vrbo / HomeAway", help: "Calendario → Sincronizar otros calendarios → Exportar este calendario" },
    { value: "avaibook", label: "Avaibook", help: "Configuración → Canales → Sincronización iCal → Exportar" },
    { value: "avantio", label: "Avantio", help: "Solicita el iCal export a tu cuenta de Avantio" },
    { value: "other", label: "Otro PMS", help: "Pega cualquier URL .ics pública de tu PMS" },
];

function IcalImporterCard() {
    const [open, setOpen] = useState(false);
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busyPid, setBusyPid] = useState(null);
    const [error, setError] = useState("");
    const [syncAllBusy, setSyncAllBusy] = useState(false);
    const [lastSyncSummary, setLastSyncSummary] = useState(null);
    // Add-source modal state
    const [addingTo, setAddingTo] = useState(null); // property object
    const [addForm, setAddForm] = useState({ source: "airbnb", url: "", label: "" });
    // Quick "create apartment from iCal" modal state
    const [quickOpen, setQuickOpen] = useState(false);
    const [quickForm, setQuickForm] = useState({
        name: "",
        url: "",
        source: "airbnb",
        bedrooms: 1,
        bathrooms: 1,
        capacity: 2,
        address: "",
    });
    const [quickBusy, setQuickBusy] = useState(false);
    const [quickResult, setQuickResult] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/properties");
            setProperties(data || []);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (open) load(); }, [open, load]);

    const submitNewSource = async () => {
        if (!addingTo) return;
        const url = addForm.url.trim();
        if (!url.startsWith("http")) {
            alert("Pega una URL iCal válida (debe empezar por http://)");
            return;
        }
        setBusyPid(addingTo.id);
        try {
            await api.post(`/properties/${addingTo.id}/ical-sources`, {
                url,
                source: addForm.source,
                label: addForm.label || null,
            });
            // Optionally sync right away
            await api.post(`/properties/${addingTo.id}/ical-sync`).catch(() => {});
            setAddingTo(null);
            setAddForm({ source: "airbnb", url: "", label: "" });
            await load();
        } catch (e) {
            alert(formatApiError(e));
        } finally {
            setBusyPid(null);
        }
    };

    const removeSource = async (pid, idx) => {
        if (!window.confirm("¿Eliminar este iCal? Las reservas ya importadas se mantienen.")) return;
        setBusyPid(pid);
        try {
            await api.delete(`/properties/${pid}/ical-sources/${idx}`);
            await load();
        } catch (e) {
            alert(formatApiError(e));
        } finally {
            setBusyPid(null);
        }
    };

    const syncProperty = async (pid) => {
        setBusyPid(pid);
        try {
            const { data } = await api.post(`/properties/${pid}/ical-sync`);
            setLastSyncSummary({
                pid,
                bookings_imported: data?.bookings_imported ?? 0,
                tasks_created: data?.tasks_created ?? 0,
            });
            await load();
        } catch (e) {
            alert(formatApiError(e));
        } finally {
            setBusyPid(null);
        }
    };

    const syncAll = async () => {
        setSyncAllBusy(true);
        try {
            const { data } = await api.post("/ical/sync-all");
            setLastSyncSummary({ all: true, ...data });
            await load();
        } catch (e) {
            alert(formatApiError(e));
        } finally {
            setSyncAllBusy(false);
        }
    };

    // Create a brand-new property in one shot from a paste-in iCal URL.
    const submitQuickCreate = async () => {
        if (!quickForm.name.trim() || !quickForm.url.trim().startsWith("http")) {
            alert("Necesito un nombre y una URL iCal válida (http/https).");
            return;
        }
        setQuickBusy(true);
        try {
            // 1) Generate next free code in the IC- (iCal-only) series
            const existingCodes = (properties || [])
                .map((p) => p.code)
                .filter((c) => c && c.startsWith("IC-"));
            const nextNum = existingCodes.length === 0
                ? 1
                : Math.max(...existingCodes.map((c) => parseInt(c.split("-")[1], 10) || 0)) + 1;
            const code = `IC-${String(nextNum).padStart(3, "0")}`;
            // 2) Create the property
            const { data: prop } = await api.post("/properties", {
                code,
                name: quickForm.name.trim(),
                address: quickForm.address.trim() || "",
                type: "apartment",
                bedrooms: Number(quickForm.bedrooms) || 1,
                bathrooms: Number(quickForm.bathrooms) || 1,
                capacity: Number(quickForm.capacity) || 2,
                state: "free",
                notes: "Creado desde iCal directo",
            });
            // 3) Attach the iCal feed
            await api.post(`/properties/${prop.id}/ical-sources`, {
                url: quickForm.url.trim(),
                source: quickForm.source,
                label: null,
            });
            // 4) Sync immediately
            const { data: sync } = await api.post(`/properties/${prop.id}/ical-sync`).catch(() => ({ data: {} }));
            setQuickResult({
                code,
                name: prop.name,
                bookings_imported: sync?.bookings_imported || 0,
                tasks_created: sync?.tasks_created || 0,
            });
            setQuickForm({ name: "", url: "", source: "airbnb", bedrooms: 1, bathrooms: 1, capacity: 2, address: "" });
            await load();
        } catch (e) {
            alert(formatApiError(e));
        } finally {
            setQuickBusy(false);
        }
    };

    const propsWithFeeds = properties.filter((p) => (p.ical_sources || []).length > 0);
    const totalFeeds = propsWithFeeds.reduce((acc, p) => acc + (p.ical_sources || []).length, 0);

    return (
        <div className="zentra-card-static p-5" data-testid="ical-importer-card">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <p className="text-[10px] font-semibold tracking-widest uppercase text-zentra-accent-strong">
                        Puente sin credenciales de API
                    </p>
                    <h3 className="font-display font-bold text-lg text-zentra-ink mt-1">
                        Importar reservas vía iCal
                    </h3>
                    <p className="text-sm text-zentra-muted mt-1 max-w-2xl">
                        Mientras esperas las APIs oficiales, pega aquí la URL iCal que cada PMS te ofrece por
                        propiedad. ZENTRA sincroniza cada 30 minutos y crea las limpiezas de salida automáticamente.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        type="button"
                        onClick={() => { setQuickResult(null); setQuickOpen(true); }}
                        className="zentra-btn-ghost text-sm"
                        data-testid="ical-quick-create-btn"
                    >
                        <Plus size={14} /> Apartamento suelto
                    </button>
                    <button
                        type="button"
                        onClick={syncAll}
                        disabled={syncAllBusy}
                        className="zentra-btn-ghost text-sm"
                        data-testid="ical-sync-all-btn"
                    >
                        <RefreshCw size={14} className={syncAllBusy ? "animate-spin" : ""} />
                        {syncAllBusy ? "Sincronizando…" : "Sincronizar todas ahora"}
                    </button>
                    <button
                        type="button"
                        onClick={() => setOpen(!open)}
                        className="zentra-btn-primary text-sm"
                        data-testid="ical-importer-toggle"
                    >
                        {open ? "Ocultar gestor" : "Abrir gestor"}
                    </button>
                </div>
            </div>

            <div className="mt-3 flex items-center gap-4 text-xs">
                <span className="inline-flex items-center gap-1.5 text-zentra-muted">
                    <CheckCircle2 size={12} className="text-zentra-accent-strong" />
                    <strong className="text-zentra-ink">{propsWithFeeds.length}</strong>/{properties.length} propiedades con iCal
                </span>
                <span className="inline-flex items-center gap-1.5 text-zentra-muted">
                    <BookOpen size={12} />
                    <strong className="text-zentra-ink">{totalFeeds}</strong> canales conectados
                </span>
            </div>

            {lastSyncSummary && (
                <div className="mt-3 rounded-md border border-zentra-accent/30 bg-zentra-accent/5 px-3 py-2 text-xs text-zentra-ink">
                    {lastSyncSummary.all ? (
                        <>
                            Sincronización global completada — <strong>{lastSyncSummary.synced ?? 0}</strong> propiedades procesadas ·{" "}
                            <strong>{lastSyncSummary.bookings_imported ?? 0}</strong> reservas nuevas ·{" "}
                            <strong>{lastSyncSummary.tasks_created ?? 0}</strong> limpiezas auto-generadas.
                        </>
                    ) : (
                        <>
                            Sincronización OK — <strong>{lastSyncSummary.bookings_imported ?? 0}</strong> reservas nuevas ·{" "}
                            <strong>{lastSyncSummary.tasks_created ?? 0}</strong> limpiezas creadas.
                        </>
                    )}
                </div>
            )}

            {open && (
                <div className="mt-4">
                    {loading ? (
                        <div className="zentra-skeleton h-32" />
                    ) : properties.length === 0 ? (
                        <p className="text-sm text-zentra-muted">Sin propiedades. Crea una primero.</p>
                    ) : (
                        <div className="border border-zentra-border rounded-md overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-zentra-bg/60 border-b border-zentra-border text-[10px] uppercase tracking-widest text-zentra-muted">
                                    <tr>
                                        <th className="text-left px-3 py-2.5 font-semibold">Propiedad</th>
                                        <th className="text-left px-3 py-2.5 font-semibold">Canales iCal</th>
                                        <th className="text-right px-3 py-2.5 font-semibold w-44">Acción</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {properties.map((p) => {
                                        const feeds = p.ical_sources || [];
                                        return (
                                            <tr key={p.id} className="border-b border-zentra-border last:border-b-0">
                                                <td className="px-3 py-2.5 align-top">
                                                    <div className="font-medium text-zentra-ink truncate max-w-[260px]">{p.name}</div>
                                                    <div className="text-[10px] text-zentra-muted font-mono-zentra">{p.code}</div>
                                                </td>
                                                <td className="px-3 py-2.5">
                                                    {feeds.length === 0 ? (
                                                        <span className="text-xs text-zentra-muted italic">— Sin canales iCal —</span>
                                                    ) : (
                                                        <ul className="space-y-1">
                                                            {feeds.map((f, idx) => (
                                                                <li key={idx} className="flex items-center gap-2 text-xs">
                                                                    <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${
                                                                        f.last_status === "ok"
                                                                            ? "bg-emerald-100 text-emerald-900 border border-emerald-300"
                                                                            : f.last_status === "error"
                                                                                ? "bg-red-100 text-red-900 border border-red-300"
                                                                                : "bg-zinc-100 text-zinc-700 border border-zinc-300"
                                                                    }`}>
                                                                        {ICAL_SOURCES.find((s) => s.value === f.source)?.label || f.source}
                                                                    </span>
                                                                    <span className="text-zentra-muted text-[11px] font-mono-zentra truncate max-w-[280px]" title={f.url}>
                                                                        {f.url}
                                                                    </span>
                                                                    {f.last_synced && (
                                                                        <span className="text-[10px] text-zentra-muted shrink-0">
                                                                            · {String(f.last_synced).slice(0, 16).replace("T", " ")}
                                                                        </span>
                                                                    )}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => removeSource(p.id, idx)}
                                                                        className="ml-auto text-red-700 hover:bg-red-50 rounded p-1"
                                                                        title="Eliminar este iCal"
                                                                    >
                                                                        <Trash2 size={11} />
                                                                    </button>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2.5 text-right align-top">
                                                    <div className="inline-flex items-center gap-1.5 justify-end">
                                                        <button
                                                            type="button"
                                                            onClick={() => { setAddingTo(p); setAddForm({ source: "airbnb", url: "", label: "" }); }}
                                                            className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1.5 rounded-md border border-zentra-border bg-white hover:border-zentra-primary"
                                                            data-testid={`ical-add-${p.code}`}
                                                        >
                                                            <Plus size={11} /> iCal
                                                        </button>
                                                        {feeds.length > 0 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => syncProperty(p.id)}
                                                                disabled={busyPid === p.id}
                                                                className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1.5 rounded-md border border-zentra-border bg-white hover:border-zentra-primary disabled:opacity-50"
                                                                data-testid={`ical-sync-${p.code}`}
                                                            >
                                                                <RefreshCw size={11} className={busyPid === p.id ? "animate-spin" : ""} />
                                                                Sync
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Quick "create apartment from iCal URL" modal */}
            <Modal
                open={quickOpen}
                onClose={() => { setQuickOpen(false); setQuickResult(null); }}
                title="Apartamento suelto · crear desde iCal"
                subtitle="Pega el nombre y la URL iCal. ZENTRA crea la propiedad y sincroniza las reservas al instante."
                wide
                testId="ical-quick-modal"
                footer={
                    quickResult ? (
                        <button
                            onClick={() => { setQuickOpen(false); setQuickResult(null); }}
                            className="zentra-btn-primary"
                            data-testid="ical-quick-close"
                        >
                            Hecho
                        </button>
                    ) : (
                        <>
                            <button onClick={() => setQuickOpen(false)} className="zentra-btn-ghost" data-testid="ical-quick-cancel">
                                Cancelar
                            </button>
                            <button
                                onClick={submitQuickCreate}
                                disabled={quickBusy || !quickForm.name || !quickForm.url}
                                className="zentra-btn-primary"
                                data-testid="ical-quick-save"
                            >
                                {quickBusy ? "Creando…" : "Crear y sincronizar"}
                            </button>
                        </>
                    )
                }
            >
                {quickResult ? (
                    <div className="rounded-md border border-zentra-accent/30 bg-zentra-accent/5 p-4 text-sm">
                        <p className="font-display font-semibold text-zentra-ink">
                            ✓ Apartamento creado: <span className="font-mono-zentra text-zentra-accent-strong">{quickResult.code}</span> · {quickResult.name}
                        </p>
                        <p className="text-zentra-muted mt-1">
                            Se han importado <strong className="text-zentra-ink">{quickResult.bookings_imported}</strong> reservas
                            y creado <strong className="text-zentra-ink">{quickResult.tasks_created}</strong> tareas de limpieza.
                        </p>
                        <p className="text-[11px] text-zentra-muted mt-2">
                            Sigue añadiendo más apartamentos sin cerrar el modal: pulsa de nuevo "Apartamento suelto".
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div className="grid md:grid-cols-2 gap-3">
                            <div>
                                <label className="zentra-label">Nombre del apartamento *</label>
                                <input
                                    className="zentra-input"
                                    value={quickForm.name}
                                    onChange={(e) => setQuickForm({ ...quickForm, name: e.target.value })}
                                    placeholder="Ej. Loft Malasaña centro"
                                    data-testid="ical-quick-name"
                                />
                            </div>
                            <div>
                                <label className="zentra-label">Canal / fuente</label>
                                <select
                                    className="zentra-input"
                                    value={quickForm.source}
                                    onChange={(e) => setQuickForm({ ...quickForm, source: e.target.value })}
                                    data-testid="ical-quick-source"
                                >
                                    {ICAL_SOURCES.map((s) => (
                                        <option key={s.value} value={s.value}>{s.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="zentra-label">URL iCal *</label>
                            <input
                                className="zentra-input font-mono-zentra text-xs"
                                value={quickForm.url}
                                onChange={(e) => setQuickForm({ ...quickForm, url: e.target.value })}
                                placeholder="https://www.airbnb.es/calendar/ical/12345.ics?s=…"
                                data-testid="ical-quick-url"
                            />
                            <p className="text-[11px] text-zentra-muted mt-1">
                                {ICAL_SOURCES.find((s) => s.value === quickForm.source)?.help}
                            </p>
                        </div>
                        <div>
                            <label className="zentra-label">Dirección (opcional)</label>
                            <input
                                className="zentra-input"
                                value={quickForm.address}
                                onChange={(e) => setQuickForm({ ...quickForm, address: e.target.value })}
                                placeholder="Calle, número, ciudad"
                            />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <label className="zentra-label">Dormitorios</label>
                                <input
                                    type="number"
                                    min="0"
                                    className="zentra-input"
                                    value={quickForm.bedrooms}
                                    onChange={(e) => setQuickForm({ ...quickForm, bedrooms: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="zentra-label">Baños</label>
                                <input
                                    type="number"
                                    min="0"
                                    className="zentra-input"
                                    value={quickForm.bathrooms}
                                    onChange={(e) => setQuickForm({ ...quickForm, bathrooms: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="zentra-label">Capacidad (pax)</label>
                                <input
                                    type="number"
                                    min="1"
                                    className="zentra-input"
                                    value={quickForm.capacity}
                                    onChange={(e) => setQuickForm({ ...quickForm, capacity: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Add-source modal */}
            <Modal
                open={!!addingTo}
                onClose={() => setAddingTo(null)}
                title={addingTo ? `Conectar iCal — ${addingTo.name}` : ""}
                subtitle="Pega la URL iCal que el PMS te da en su panel. La sincronización ocurre cada 30 min."
                wide
                testId="ical-add-modal"
                footer={
                    <>
                        <button onClick={() => setAddingTo(null)} className="zentra-btn-ghost" data-testid="ical-add-cancel">
                            Cancelar
                        </button>
                        <button
                            onClick={submitNewSource}
                            disabled={!addForm.url || busyPid === addingTo?.id}
                            className="zentra-btn-primary"
                            data-testid="ical-add-save"
                        >
                            {busyPid === addingTo?.id ? "Guardando…" : "Conectar y sincronizar"}
                        </button>
                    </>
                }
            >
                <div className="space-y-3">
                    <div>
                        <label className="zentra-label">Canal / fuente</label>
                        <select
                            className="zentra-input"
                            value={addForm.source}
                            onChange={(e) => setAddForm({ ...addForm, source: e.target.value })}
                            data-testid="ical-add-source"
                        >
                            {ICAL_SOURCES.map((s) => (
                                <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                        </select>
                        <p className="text-[11px] text-zentra-muted mt-1">
                            {ICAL_SOURCES.find((s) => s.value === addForm.source)?.help}
                        </p>
                    </div>
                    <div>
                        <label className="zentra-label">URL iCal</label>
                        <input
                            className="zentra-input font-mono-zentra text-xs"
                            value={addForm.url}
                            onChange={(e) => setAddForm({ ...addForm, url: e.target.value })}
                            placeholder="https://www.airbnb.es/calendar/ical/12345.ics?s=…"
                            data-testid="ical-add-url"
                        />
                    </div>
                    <div>
                        <label className="zentra-label">Etiqueta (opcional)</label>
                        <input
                            className="zentra-input"
                            value={addForm.label}
                            onChange={(e) => setAddForm({ ...addForm, label: e.target.value })}
                            placeholder={`Ej. ${ICAL_SOURCES.find((s) => s.value === addForm.source)?.label || "Mi canal"}`}
                        />
                    </div>
                </div>
            </Modal>
        </div>
    );
}

// ======================================================================
// Bots PMS — web-scraping bots for PMS without API access.
// Multi-agency: one connection per (PMS, agency_credentials).
// Cron runs 08:00 + 13:00 Madrid. Manual run + 2FA flow supported.
// ======================================================================
function BotsPmsCard() {
    const [connectors, setConnectors] = useState([]);
    const [connections, setConnections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);
    const [addType, setAddType] = useState("");
    const [addForm, setAddForm] = useState({});
    const [busyId, setBusyId] = useState(null);
    const [twofaPrompt, setTwofaPrompt] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [c, k] = await Promise.all([
                api.get("/bots/connectors"),
                api.get("/bots/connections"),
            ]);
            setConnectors(c.data || []);
            setConnections(k.data || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const connectorMap = useMemo(
        () => Object.fromEntries(connectors.map((c) => [c.type, c])),
        [connectors],
    );

    const openCreate = () => {
        setAddType(connectors[0]?.type || "");
        setAddForm({});
        setAddOpen(true);
    };

    const submitCreate = async () => {
        const info = connectorMap[addType];
        if (!info) return;
        const missing = info.credentials_schema
            .filter((f) => f.required)
            .filter((f) => !addForm[f.key]?.toString().trim());
        if (missing.length > 0) {
            alert(`Faltan campos: ${missing.map((m) => m.label).join(", ")}`);
            return;
        }
        const name = (addForm.agency_label || "").trim() || `${info.label} sin nombre`;
        try {
            await api.post("/bots/connections", {
                type: addType,
                name: `${info.label} · ${name}`,
                credentials: addForm,
                enabled: true,
            });
            setAddOpen(false);
            setAddForm({});
            load();
        } catch (e) {
            alert(formatApiError(e));
        }
    };

    const runNow = async (conn) => {
        setBusyId(conn.id);
        try {
            const { data } = await api.post(`/bots/connections/${conn.id}/run`, {});
            if (data.needs_2fa) {
                setTwofaPrompt({ conn, screenshot_b64: data.screenshot_b64, message: data.message });
            } else if (data.ok) {
                alert(`✓ ${data.message || "Bot ejecutado"}`);
            } else {
                alert(`✗ ${data.error || data.message || "Error desconocido"}`);
            }
            load();
        } catch (e) {
            alert(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    const submit2fa = async (code) => {
        if (!twofaPrompt) return;
        setBusyId(twofaPrompt.conn.id);
        try {
            const { data } = await api.post(`/bots/connections/${twofaPrompt.conn.id}/run`, { twofa_code: code });
            setTwofaPrompt(null);
            if (data.ok) {
                alert(`✓ ${data.message || "Login con 2FA OK"}`);
            } else {
                alert(`✗ ${data.error || data.message || "Error 2FA"}`);
            }
            load();
        } catch (e) {
            alert(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    const toggleEnabled = async (conn) => {
        await api.patch(`/bots/connections/${conn.id}`, { enabled: !conn.enabled });
        load();
    };

    const remove = async (conn) => {
        if (!window.confirm(`¿Eliminar el bot "${conn.name}"? Las propiedades y reservas ya importadas se mantienen.`)) return;
        await api.delete(`/bots/connections/${conn.id}`);
        load();
    };

    return (
        <div className="zentra-card-static p-5" data-testid="bots-pms-card">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <p className="text-[10px] font-semibold tracking-widest uppercase text-zentra-accent-strong">
                        Automatización avanzada · Aceptas los riesgos
                    </p>
                    <h3 className="font-display font-bold text-lg text-zentra-ink mt-1">
                        Bots PMS (login web automatizado)
                    </h3>
                    <p className="text-sm text-zentra-muted mt-1 max-w-2xl">
                        Para agencias que no te dan API ni iCal. ZENTRA entra al panel del PMS con tus credenciales
                        cifradas a las 08:00 y 13:00 (hora Madrid) e importa propiedades + reservas.
                        Soporta 2FA por email.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={openCreate}
                    className="zentra-btn-primary text-sm"
                    data-testid="bot-add-btn"
                >
                    <Plus size={14} /> Añadir bot
                </button>
            </div>

            <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2 text-xs inline-flex items-start gap-2">
                <AlertCircle size={13} className="mt-0.5 shrink-0" />
                <span>
                    El PMS puede bloquear la cuenta si detecta automatización. Cambios en su HTML romperán el bot
                    hasta que lo arreglemos. Mantén tus credenciales actualizadas.
                </span>
            </div>

            <div className="mt-4">
                {loading ? (
                    <div className="zentra-skeleton h-24" />
                ) : connections.length === 0 ? (
                    <div className="text-center py-8 text-sm text-zentra-muted">
                        Sin bots configurados. Pulsa "Añadir bot" para empezar.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {connections.map((conn) => {
                            const meta = conn.credentials_meta || {};
                            return (
                                <div
                                    key={conn.id}
                                    className="border border-zentra-border rounded-md p-3 flex items-center gap-3 flex-wrap"
                                    data-testid={`bot-row-${conn.id}`}
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-display font-semibold text-zentra-ink text-sm truncate">
                                                {conn.name}
                                            </span>
                                            <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-zentra-bg border border-zentra-border text-zentra-muted">
                                                {conn.type}
                                            </span>
                                            {!conn.enabled && (
                                                <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-zinc-200 text-zinc-700">
                                                    Pausado
                                                </span>
                                            )}
                                            {conn.needs_2fa && (
                                                <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300">
                                                    Espera código 2FA
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-zentra-muted mt-0.5 font-mono-zentra truncate">
                                            {meta.email || meta.hotel_id || "—"}
                                            {meta.login_url ? ` · ${meta.login_url}` : ""}
                                        </p>
                                        {conn.last_run_at && (
                                            <p className="text-[11px] text-zentra-muted mt-1">
                                                Última ejecución:{" "}
                                                <span className="font-mono-zentra">
                                                    {String(conn.last_run_at).slice(0, 16).replace("T", " ")}
                                                </span>
                                                {" — "}
                                                {conn.last_run_ok ? (
                                                    <span className="text-emerald-700 font-semibold">OK</span>
                                                ) : (
                                                    <span className="text-red-700 font-semibold">
                                                        ERROR{conn.last_run_error ? `: ${conn.last_run_error.slice(0, 80)}` : ""}
                                                    </span>
                                                )}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <button
                                            type="button"
                                            onClick={() => runNow(conn)}
                                            disabled={busyId === conn.id}
                                            className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-md border border-zentra-border bg-white hover:border-zentra-primary disabled:opacity-50"
                                            data-testid={`bot-run-${conn.id}`}
                                        >
                                            <RefreshCw size={12} className={busyId === conn.id ? "animate-spin" : ""} />
                                            {busyId === conn.id ? "Ejecutando…" : "Ejecutar ahora"}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => toggleEnabled(conn)}
                                            className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1.5 rounded-md border border-zentra-border bg-white hover:border-zentra-primary"
                                        >
                                            {conn.enabled ? <Pause size={12} /> : <Play size={12} />}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => remove(conn)}
                                            className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1.5 rounded-md border border-red-200 text-red-700 bg-white hover:bg-red-50"
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <Modal
                open={addOpen}
                onClose={() => setAddOpen(false)}
                title="Añadir bot PMS"
                subtitle="Las credenciales se cifran en la base de datos y no se vuelven a mostrar."
                wide
                testId="bot-add-modal"
                footer={
                    <>
                        <button onClick={() => setAddOpen(false)} className="zentra-btn-ghost" data-testid="bot-add-cancel">Cancelar</button>
                        <button onClick={submitCreate} className="zentra-btn-primary" data-testid="bot-add-save">
                            Crear bot
                        </button>
                    </>
                }
            >
                <div className="space-y-3">
                    <div>
                        <label className="zentra-label">Tipo de PMS</label>
                        <select
                            className="zentra-input"
                            value={addType}
                            onChange={(e) => { setAddType(e.target.value); setAddForm({}); }}
                            data-testid="bot-add-type"
                        >
                            {connectors.map((c) => (
                                <option key={c.type} value={c.type}>{c.label}</option>
                            ))}
                        </select>
                        <p className="text-[11px] text-zentra-muted mt-1">
                            {connectorMap[addType]?.description}
                        </p>
                    </div>
                    {connectorMap[addType]?.credentials_schema?.map((f) => (
                        <div key={f.key}>
                            <label className="zentra-label">
                                {f.label}{f.required ? "" : <span className="text-zentra-muted ml-1">(opcional)</span>}
                            </label>
                            <input
                                type={f.type === "password" ? "password" : "text"}
                                className="zentra-input"
                                placeholder={f.placeholder}
                                value={addForm[f.key] || ""}
                                onChange={(e) => setAddForm({ ...addForm, [f.key]: e.target.value })}
                                data-testid={`bot-add-field-${f.key}`}
                            />
                            {f.help && <p className="text-[11px] text-zentra-muted mt-1">{f.help}</p>}
                        </div>
                    ))}
                </div>
            </Modal>

            <Modal
                open={!!twofaPrompt}
                onClose={() => setTwofaPrompt(null)}
                title="Código 2FA requerido"
                subtitle={twofaPrompt?.message || "Tu PMS ha enviado un código por email. Pégalo aquí."}
                testId="bot-2fa-modal"
                footer={null}
            >
                {twofaPrompt && (
                    <Twofa2FAForm
                        screenshotB64={twofaPrompt.screenshot_b64}
                        onSubmit={submit2fa}
                        onCancel={() => setTwofaPrompt(null)}
                    />
                )}
            </Modal>
        </div>
    );
}

function Twofa2FAForm({ screenshotB64, onSubmit, onCancel }) {
    const [code, setCode] = useState("");
    return (
        <div className="space-y-3">
            {screenshotB64 && (
                <div className="border border-zentra-border rounded-md p-2 bg-zentra-bg">
                    <p className="text-[10px] uppercase tracking-widest font-semibold text-zentra-muted mb-1">Captura del PMS</p>
                    <img
                        src={`data:image/png;base64,${screenshotB64}`}
                        alt="Captura del PMS pidiendo 2FA"
                        className="max-h-64 w-full object-contain rounded"
                    />
                </div>
            )}
            <div>
                <label className="zentra-label">Código recibido</label>
                <input
                    autoFocus
                    type="text"
                    inputMode="numeric"
                    className="zentra-input font-mono-zentra text-lg tracking-widest text-center"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/[^0-9A-Za-z]/g, "").slice(0, 10))}
                    placeholder="ej. 123456"
                    data-testid="bot-2fa-code"
                />
            </div>
            <div className="flex justify-end gap-2 pt-2">
                <button onClick={onCancel} className="zentra-btn-ghost" data-testid="bot-2fa-cancel">Cancelar</button>
                <button
                    onClick={() => onSubmit(code)}
                    disabled={code.length < 4}
                    className="zentra-btn-primary"
                    data-testid="bot-2fa-submit"
                >
                    Continuar
                </button>
            </div>
        </div>
    );
}

